import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { getPublishedRoadmaps, toggleRoadmapLike, checkRoadmapLike, incrementRoadmapViews } from '../services/supabase'
import useStore from '../store/useStore'

const RoadmapExchange = () => {
  const navigate = useNavigate()
  const { user } = useStore()
  const [roadmaps, setRoadmaps] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTags, setSelectedTags] = useState([])
  const [likedRoadmaps, setLikedRoadmaps] = useState(new Set())

  const popularTags = [
    'Python', 'JavaScript', 'React', 'Web Development', 'Data Science',
    'Machine Learning', 'Mobile Development', 'DevOps', 'Design', 'Business'
  ]

  useEffect(() => {
    fetchRoadmaps()
  }, [searchQuery, selectedTags])

  const fetchRoadmaps = async () => {
    setLoading(true)
    const { data, error } = await getPublishedRoadmaps({
      search: searchQuery,
      tags: selectedTags
    })

    if (!error && data) {
      setRoadmaps(data)
      
      // Check which roadmaps user has liked
      if (user) {
        const likedSet = new Set()
        for (const roadmap of data) {
          const { liked } = await checkRoadmapLike(roadmap.id)
          if (liked) likedSet.add(roadmap.id)
        }
        setLikedRoadmaps(likedSet)
      }
    }
    setLoading(false)
  }

  const handleLike = async (roadmapId) => {
    if (!user) {
      navigate('/auth')
      return
    }

    const { liked, error } = await toggleRoadmapLike(roadmapId)
    if (!error) {
      setLikedRoadmaps(prev => {
        const newSet = new Set(prev)
        if (liked) {
          newSet.add(roadmapId)
        } else {
          newSet.delete(roadmapId)
        }
        return newSet
      })

      // Update likes count in UI
      setRoadmaps(prev => prev.map(r => 
        r.id === roadmapId 
          ? { ...r, likes_count: r.likes_count + (liked ? 1 : -1) }
          : r
      ))
    }
  }

  const handleViewRoadmap = async (roadmap) => {
    await incrementRoadmapViews(roadmap.id)
    navigate(`/roadmap/${roadmap.id}`)
  }

  const toggleTag = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    )
  }

  return (
    <div className="gradient-bg">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-black text-gray-900 mb-2">
                🌟 Roadmap Exchange
              </h1>
              <p className="text-gray-600">
                Discover and share learning roadmaps created by the community
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 bg-gradient-to-r from-violet-600 to-cyan-600 text-white rounded-xl font-bold hover:shadow-lg transition-all"
            >
              Back to Dashboard
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search roadmaps by title, topic, or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-6 py-4 pl-14 bg-white border-2 border-gray-200 rounded-2xl focus:border-violet-500 focus:ring-4 focus:ring-violet-100 transition-all text-lg"
            />
            <svg
              className="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Tags Filter */}
          <div className="mt-4 flex flex-wrap gap-2">
            {popularTags.map(tag => (
              <button
                key={tag}
                onClick={() => toggleTag(tag)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  selectedTags.includes(tag)
                    ? 'bg-gradient-to-r from-violet-600 to-cyan-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border-2 border-gray-200'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-violet-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Loading roadmaps...</p>
            </div>
          </div>
        ) : roadmaps.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">📭</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No roadmaps found</h3>
            <p className="text-gray-600">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {roadmaps.map((roadmap, idx) => (
                <motion.div
                  key={roadmap.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: idx * 0.05 }}
                  className="card p-6 hover:shadow-2xl transition-all cursor-pointer group relative overflow-hidden"
                  onClick={() => handleViewRoadmap(roadmap)}
                >
                  {/* Gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-violet-50 to-cyan-50 opacity-0 group-hover:opacity-100 transition-opacity" />

                  <div className="relative z-10">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-black text-gray-900 mb-2 group-hover:text-violet-700 transition-colors line-clamp-2">
                          {roadmap.title}
                        </h3>
                        <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                          {roadmap.description || roadmap.topic}
                        </p>
                      </div>
                    </div>

                    {/* Author */}
                    <div className="flex items-center gap-2 mb-4 p-3 bg-gradient-to-r from-violet-50 to-cyan-50 rounded-lg">
                      <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold">
                        {roadmap.author_name?.[0]?.toUpperCase() || 'A'}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          {roadmap.author_name || 'Anonymous'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {roadmap.is_ai_generated ? 'AI-Generated' : 'User-Created'}
                        </p>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-4 mb-4">
                      <span className="flex items-center gap-1 text-sm text-gray-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {roadmap.duration_weeks} weeks
                      </span>
                      <span className="flex items-center gap-1 text-sm text-gray-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        {roadmap.views_count || 0}
                      </span>
                    </div>

                    {/* Tags */}
                    {roadmap.tags && roadmap.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {roadmap.tags.slice(0, 3).map(tag => (
                          <span
                            key={tag}
                            className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-semibold"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Like Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleLike(roadmap.id)
                      }}
                      className={`w-full py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
                        likedRoadmaps.has(roadmap.id)
                          ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <svg
                        className="w-5 h-5"
                        fill={likedRoadmaps.has(roadmap.id) ? 'currentColor' : 'none'}
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      {roadmap.likes_count || 0} Likes
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>
    </div>
  )
}

export default RoadmapExchange
